import React, {Children, isValidElement, PropsWithChildren, ReactElement, ReactNode} from 'react'
import computeLayout, {LayoutNode, LayoutStyle} from 'css-layout'
import {StyleProps} from '../../ui'
import {notNull} from '../../type'
import {sum} from '../../agg'
import {SVGProps} from 'react'
import {match, P} from 'ts-pattern'
import {flat} from 'remeda'
import {ForwardedRef} from 'react'
// import {findDOMNode} from 'react-dom'

// originally from this repo https://github.dev/zoopoetics/react-svg-flexbox
export interface GComponent {
  x?: number
  y?: number
}

export type GProps = PropsWithChildren<StyleProps> & {
  onLayout?: (layout: LayoutNode) => void
  x?: number
  y?: number
  debug?: boolean
  forwardedRef?: ForwardedRef<SVGGElement>
}

interface DOMRect {
  readonly bottom: number
  readonly height: number
  readonly left: number
  readonly right: number
  readonly top: number
  readonly width: number
  readonly x: number
  readonly y: number
}

type HasBBox = {getBBox: (options?: SVGBoundingBoxOptions) => DOMRect}
const hasBBox = (b: any): b is HasBBox => (b as Partial<HasBBox>)?.getBBox != null

const unwrapFragment = (n: ReactNode) =>
  match(n)
    .with({type: React.Fragment}, (f: any) => f.props.children)
    .otherwise(() => n)

export class G extends React.Component<GProps, {layout: LayoutNode}> {
  childRefs: any[]
  shouldUpdateAgain: boolean
  constructor(props: GProps) {
    super(props)
    this.childRefs = []
    this.shouldUpdateAgain = false
    this.state = {
      layout: {
        children: [],
        style: {}
      }
    }
  }

  componentDidMount() {
    // Force update for initial layout.
    this.forceUpdate()
  }

  componentDidUpdate() {
    /*
    SVG elements must be added to the DOM before they
    can be measured. This means that each update
    requires two render passes: one to add elements
    to the DOM, another to measure them and do layout.
    SVG elements cannot be measured before they've
    been added to the DOM. To this end, we use a simple
    on/off switch to determine whether or not we should
    set layout on state again.
    */
    this.shouldUpdateAgain = !this.shouldUpdateAgain

    if (this.shouldUpdateAgain) {
      const {children, onLayout, style} = this.props

      // Measure child elements.
      const childrenMeasured = this.getChildrenMeasured(this.childRefs)

      // Merge measurements with passed styles.
      const flattenedChildren = this.getFlattenedChildren(children)
      const childrenAsMergedStyles = this.getChildrenAsMergedStyles(flattenedChildren, childrenMeasured)

      // Compute layout.
      const layout = this.getComputedLayout(childrenAsMergedStyles, style)

      if (this.props.debug) console.log('layout', {layout, childrenAsMergedStyles})

      // Pass layout to any concerned parent.
      onLayout?.(layout as any)

      // Trigger an update with the new layout.
      /* eslint-disable react/no-did-update-set-state */
      this.setState({layout})
      /* eslint-enable react/no-did-update-set-state */
    }
  }

  /*
  Returns children flattened into a single array.

  If this component is asked to lay out a combination
  of individually declared components/elements, and
  an array of the same returned from a map operation,
  React will throw an error. This can be prevented
  by flattening children out into a single array.
  */
  getFlattenedChildren(children: ReactNode): ReactElement[] {
    return flat(
      Children.toArray(unwrapFragment(children))
        .filter(isValidElement)
        .map(child => {
          return Array.isArray(child) ? child : [child]
        })
        .map(unwrapFragment)
    )
  }

  /*
  Returns child props merged with child measurements.

  If the child has explicit styles for width and height,
  those will be used. If those styles are not present,
  we will use measured widths and heights.
  */
  getChildrenAsMergedStyles(children: ReactElement[], childrenMeasured: (DOMRect | undefined)[]): LayoutNode[] {
    return childrenMeasured.map((childMeasured, index) => {
      const child = children[index] || {}
      const style = match(child)
        .with({props: {style: P.select({})}}, s => s)
        .otherwise(() => ({})) as LayoutStyle

      const measure = (dim: 'width' | 'height') => {
        let wh = childMeasured?.[dim]
        let xy: number | undefined = childMeasured?.[dim == 'width' ? 'x' : 'y']

        // labels are often negative y & height = 0. So pretend we are starting at 0
        // if (xy != null && xy < 0) {
        //   wh = (wh ?? 0) - xy
        //   xy = 0
        // }

        const portions = [wh, xy].filter(notNull)
        return portions.length > 0 ? sum(portions) : style[dim]
      }

      const merged = {style: {...style, height: measure('height'), width: measure('width')}}

      if (this.props.debug) console.log('getChildrenAsMergedStyles', {merged, childMeasured, style})

      return merged
    })
  }

  /*
  Returns width and height measurements for children.

  You can pass explicit width and height styles for child elements,
  but usually it's most convenient to let this component measure
  them for you and use those numbers for layout.
  */
  getChildrenMeasured(childRefs: SVGElement[]) {
    /* eslint-disable react/no-find-dom-node */
    return childRefs && childRefs.length ?
        childRefs.map(childRef => {
          if (hasBBox(childRef)) return childRef.getBBox()
          // const childNode = findDOMNode(childRef)
          // if (hasBBox(childNode)) return childNode.getBBox()
          return undefined
        })
      : []
    /* eslint-enable react/no-find-dom-node */
  }

  /*
  Returns values computed by css-layout.
  */
  getComputedLayout(childrenWithMergedStyles: LayoutNode[], style?: LayoutStyle) {
    const layout: LayoutNode = {
      children: childrenWithMergedStyles,
      style: style ?? {}
    }
    computeLayout(layout)
    return layout
  }

  /*
  Digs into a layout object and returns the array of children.
  */
  getLayoutChildren(layout: LayoutNode) {
    return layout?.children ?? []
  }

  /*
  Returns an object containing layout properties for a child element.
  Different types of SVG elements require different types of layout.
  */
  getLayoutAttributesForChild(child: ReactElement, layoutChild: any) {
    if (layoutChild && layoutChild.layout) {
      const {left, top} = layoutChild.layout
      switch (child.type) {
        case 'circle': {
          /*
          Offset is used to position the circle from
          its top left corner, not its center.
          */
          const {r} = child.props as SVGProps<SVGCircleElement>
          const offset = r ?? 0
          return {
            cx: left + offset,
            cy: top + offset
          }
        }
        case 'ellipse': {
          /*
          Offset is used to position the ellipse from
          its top left corner, not its center.
          */
          const {rx, ry} = child.props as SVGProps<SVGEllipseElement>
          return {
            cx: left + (rx ?? 0),
            cy: top + (ry ?? 0)
          }
        }
        case 'g':
        case 'path':
        case 'polygon':
        case 'polyline':
          return {
            transform: `translate(${left} ${top})`
          }
        default:
          return {
            x: left,
            y: top
          }
      }
    }
    return {}
  }

  render() {
    const {children, className, x, y} = this.props
    const {layout} = this.state

    /*
    Re-create an array of refs on each render.

    We will store references to child components,
    in case they need to be measured on the next update.

    After consulting the relevant documentation -
    https://reactjs.org/docs/refs-and-the-dom.html#exposing-dom-refs-to-parent-components
    - this seemed to be the best available approach.
    */
    this.childRefs = []

    const flattenedChildren = this.getFlattenedChildren(children)
    const layoutChildren = this.getLayoutChildren(layout)

    return (
      <g
        ref={this.props.forwardedRef}
        className={className}
        transform={x || y ? `translate(${x ?? 0} ${y ?? 0})` : undefined}
        style={this.props.style}>
        {flattenedChildren.map((child, index) => {
          return React.cloneElement(child as ReactElement<{ref: any}>, {
            ...(child.props as any),
            ...this.getLayoutAttributesForChild(child, layoutChildren[index]),
            key: `child-${index}`,
            ref: (node: any) => {
              // if (this.props.debug) console.log('pushing child ref', node)
              return node ? this.childRefs.push(node) : null
            }
          })
        })}
      </g>
    )
  }
}
